// "How are these two people related?"
//
// Approach:
//  1. Blood/legal kinship via lowest common ancestors. Each LCA at (da, db) generations
//     away from A and B maps to a term (parent, sibling, first cousin once removed…).
//     Explicit sibling links without shared parents get a *virtual* shared parent so they
//     participate in the same math (their kids become nieces/nephews, etc.).
//  2. Direct partner and social links.
//  3. In-law and step relations: a partner hop glued to a blood relation.
//  4. Family friends and shared groups.
//  5. Fallback: the shortest path over every kind of link, described as a possessive
//     chain ("mother's best friend's son") built from the same segment vocabulary.
//
// All terms describe B relative to A: "B is A's <term>".

import { childrenOf, graphOf, other, parentsOf, siblingsOf, type Graph } from './graph';
import { displayName, isDeceased, termGender, type TermGender } from './people';
import type {
  Group, Id, ParentType, PartnerRel, SiblingType, SocialRel, Tree,
} from './types';

export type StepKind = 'up' | 'down' | 'partner' | 'sibling' | 'social' | 'group';

/** One hop in a relationship path, from → to. */
export interface Step {
  kind: StepKind;
  from: Id;
  to: Id;
  parentType?: ParentType;
  partner?: PartnerRel;
  siblingType?: SiblingType;
  social?: SocialRel;
  group?: Group;
}

export type Segment =
  | { kind: 'blood'; up: number; down: number; end: Id; types: ParentType[]; half?: boolean; double?: boolean }
  | { kind: 'partner'; end: Id; rel: PartnerRel }
  | { kind: 'social'; end: Id; start: Id; rel: SocialRel }
  | { kind: 'group'; end: Id; group: Group }
  | { kind: 'coparent'; end: Id };

export type RelationCategory = 'self' | 'blood' | 'partner' | 'in-law' | 'social' | 'family-friend' | 'group' | 'chain';

export interface Relation {
  category: RelationCategory;
  /** B relative to A, e.g. "second cousin once removed" or "mother's best friend". */
  term: string;
  path: Step[];
  segments: Segment[];
  commonAncestors: Id[];
  explanation: string;
  /** Lower = closer; used to choose the headline relation. */
  rank: number;
}

/**
 * How close each kind of relation counts, lower first; the Relate view explains these. Blood
 * relations count generations: up to the closest common ancestor plus down from it (a parent 1,
 * a sibling or grandparent 2, an aunt 3, a first cousin 4).
 */
export const RANK = {
  self: 0,
  /** Current or widowed. */
  partner: 0.5,
  bestFriend: 1.5,
  /** Siblings recorded as siblings rather than through shared parents. */
  recordedSibling: 2,
  /** Friends, mentors, roommates, colleagues, neighbors. */
  connection: 2.5,
  stepsibling: 2.5,
  foundFamily: 2.6,
  familyFriend: 3.5,
  sharedGroup: 5,
  formerPartner: 6,
  /** In-laws and step-relations: the blood relation they go through, plus this… */
  inLaw: 1.2,
  /** …plus this more when the partner in between isn't married or in a civil union. */
  unmarriedInLaw: 0.3,
  /** In-laws only go through blood relations this close. */
  inLawReach: 6,
  /** The last resort, a chain of any links: this plus its length. */
  chain: 10,
} as const;

export interface KinshipResult {
  a: Id;
  b: Id;
  relations: Relation[];
}


// ---------------------------------------------------------------- terms

function gendered(g: TermGender | undefined, fem: string, masc: string, neutral: string): string {
  if (g === 'feminine') return fem;
  if (g === 'masculine') return masc;
  return neutral;
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

const COUSIN_ORDINALS = ['', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];

function greats(k: number): string {
  if (k <= 0) return '';
  if (k <= 3) return 'great-'.repeat(k);
  return `${ordinal(k)} great-`;
}

function removedText(r: number): string {
  if (r === 0) return '';
  if (r === 1) return ' once removed';
  if (r === 2) return ' twice removed';
  return ` ${r} times removed`;
}

const MARRIED_LIKE = new Set(['married', 'civil-union', 'domestic']);

const QUALIFIER_SUFFIX: Record<ParentType, string> = {
  biological: '',
  unknown: '',
  adoptive: 'through adoption',
  step: 'through a step-parent',
  foster: 'through foster care',
  guardian: 'through guardianship',
  chosen: 'through chosen family',
  surrogate: 'through surrogacy',
  donor: 'through a donor',
};

/** Neutral direct-line word n generations away: parent, grandparent, great-grandparent… */
function lineTerm(n: number, one: string, grand: string): string {
  return n === 1 ? one : greats(n - 2) + grand;
}

/** Base blood term (no qualifiers). up = A's generations to the common ancestor, down = B's. */
export function bloodTerm(up: number, down: number, g: TermGender | undefined): string {
  if (up === 0 && down === 0) return 'self';
  if (up === 0) {
    if (down === 1) return gendered(g, 'daughter', 'son', 'child');
    return greats(down - 2) + gendered(g, 'granddaughter', 'grandson', 'grandchild');
  }
  if (down === 0) {
    if (up === 1) return gendered(g, 'mother', 'father', 'parent');
    return greats(up - 2) + gendered(g, 'grandmother', 'grandfather', 'grandparent');
  }
  if (up === 1 && down === 1) return gendered(g, 'sister', 'brother', 'sibling');
  // Neutral aunt/uncle and niece/nephew are spelled out: "grandparent's sibling", "sibling's child".
  if (down === 1) {
    const word = gendered(g, 'aunt', 'uncle', '');
    return word ? greats(up - 2) + word : `${lineTerm(up - 1, 'parent', 'grandparent')}'s sibling`;
  }
  if (up === 1) {
    const word = gendered(g, 'niece', 'nephew', '');
    return word ? greats(down - 2) + word : `sibling's ${lineTerm(down - 1, 'child', 'grandchild')}`;
  }
  const degree = Math.min(up, down) - 1;
  const deg = COUSIN_ORDINALS[degree] ?? ordinal(degree);
  return `${deg} cousin${removedText(Math.abs(up - down))}`;
}

function bloodSegmentTerm(s: Extract<Segment, { kind: 'blood' }>, g: TermGender | undefined): string {
  const nonBio = [...new Set(s.types.filter((t) => t !== 'biological' && t !== 'unknown'))];
  const close = s.up + s.down <= 2;
  let base = bloodTerm(s.up, s.down, g);

  if (nonBio.length === 1 && close && !(s.up === 0 && s.down === 2) && !(s.up === 2 && s.down === 0)) {
    const t = nonBio[0];
    const isParent = s.up === 1 && s.down === 0;
    const isChild = s.up === 0 && s.down === 1;
    switch (t) {
      case 'step':
        return 'step' + base;
      case 'adoptive':
        return (isChild ? 'adopted ' : 'adoptive ') + base;
      case 'foster':
      case 'chosen':
        return `${t} ${base}`;
      case 'guardian':
        if (isParent) return 'guardian';
        if (isChild) return 'ward';
        break;
      case 'surrogate':
        if (isParent) return 'surrogate (gestational carrier)';
        if (isChild) return `${base} (born through surrogacy)`;
        break;
      case 'donor':
        if (isParent) return 'donor';
        if (isChild) return `donor-conceived ${base}`;
        break;
    }
  }
  const qual = (s.double ? 'double ' : '') + (s.half ? 'half-' : '');
  // In spelled-out terms the qualifier belongs to the sibling: "parent's half-sibling".
  if (qual) base = base.includes("'s ") ? base.replace('sibling', qual + 'sibling') : qual + base;
  const suffix = nonBio.map((t) => QUALIFIER_SUFFIX[t]).filter(Boolean);
  return suffix.length ? `${base} (${suffix.join(', ')})` : base;
}

/**
 * B as A's partner. `survivor` is set when B outlived A in a widowed partnership: B is then A's
 * widow, not A's "late" wife (who is the one that died).
 */
function partnerTerm(rel: PartnerRel, g: TermGender | undefined, survivor = false): string {
  const ended = rel.status === 'divorced' || rel.status === 'ended' || rel.status === 'separated';
  let base: string;
  switch (rel.type) {
    case 'married':
      base = gendered(g, 'wife', 'husband', 'spouse');
      break;
    case 'engaged':
      base = gendered(g, 'fiancée', 'fiancé', 'fiancé(e)');
      break;
    case 'dating':
      base = gendered(g, 'girlfriend', 'boyfriend', 'partner');
      break;
    case 'civil-union':
    case 'domestic':
    case 'partner':
    case 'other':
    default:
      base = 'partner';
  }
  if (rel.label?.trim()) base = rel.label.trim();
  if (rel.status === 'separated') return `separated ${base}`;
  if (ended) return `ex-${base}`;
  if (rel.status === 'widowed') {
    if (!survivor) return `late ${base}`;
    if (rel.label?.trim()) return `surviving ${base}`;
    return rel.type === 'married' ? gendered(g, 'widow', 'widower', 'surviving spouse') : 'surviving partner';
  }
  return base;
}

/** Whether `end` outlived the other partner: only `end`'s partner is known to have died. */
function outlived(tree: Tree, rel: PartnerRel, end: Id): boolean {
  if (rel.status !== 'widowed') return false;
  const start = rel.a === end ? rel.b : rel.a;
  return !!tree.people[start] && isDeceased(tree.people[start]) && !!tree.people[end] && !isDeceased(tree.people[end]);
}

const SOCIAL_TERMS: Record<SocialRel['type'], string> = {
  friend: 'friend',
  'close-friend': 'close friend',
  'best-friend': 'best friend',
  'family-friend': 'family friend',
  mentor: 'mentor',
  roommate: 'roommate',
  colleague: 'colleague',
  neighbor: 'neighbor',
  other: 'connection',
};

function socialTerm(rel: SocialRel, start: Id, end: Id): string {
  if (rel.label?.trim()) return rel.label.trim();
  if (rel.type === 'mentor') return rel.a === end ? 'mentor' : 'mentee';
  void start;
  return SOCIAL_TERMS[rel.type];
}

const GROUP_NOUN: Record<Group['kind'], string> = {
  'friend-group': 'friend group',
  'found-family': 'found family',
  household: 'household',
  family: 'family',
  organization: 'organization',
  other: 'group',
};

function genderOf(tree: Tree, id: Id): TermGender {
  return termGender(tree.people[id]);
}

function isBlood(s: Segment | undefined, up: number, down: number): s is Extract<Segment, { kind: 'blood' }> {
  return !!s && s.kind === 'blood' && s.up === up && s.down === down;
}

function isMarriedLike(s: Segment | undefined): s is Extract<Segment, { kind: 'partner' }> {
  return !!s && s.kind === 'partner' && MARRIED_LIKE.has(s.rel.type) && s.rel.status !== 'divorced' && s.rel.status !== 'ended';
}

/** Turn segments into one phrase, merging common in-law / step patterns into single words. */
export function segmentsToTerm(tree: Tree, segs: Segment[]): string {
  const words: string[] = [];
  let i = 0;
  while (i < segs.length) {
    const s0 = segs[i];
    const s1 = segs[i + 1];
    const s2 = segs[i + 2];
    const gEnd = (s: Segment) => genderOf(tree, s.end);
    const plainBlood = (s: Segment | undefined) =>
      s?.kind === 'blood' && s.types.every((t) => t === 'biological' || t === 'unknown');

    // parent → their spouse → that person's child = stepsibling
    if (isBlood(s0, 1, 0) && isMarriedLike(s1) && isBlood(s2, 0, 1)) {
      words.push('step' + gendered(gEnd(s2), 'sister', 'brother', 'sibling'));
      i += 3;
      continue;
    }
    if (s0?.kind === 'partner' && isMarriedLike(s0) && plainBlood(s1)) {
      const b = s1 as Extract<Segment, { kind: 'blood' }>;
      const g = gEnd(b);
      let w: string | undefined;
      if (b.up === 1 && b.down === 0) w = gendered(g, 'mother', 'father', 'parent') + '-in-law';
      else if (b.up === 2 && b.down === 0) w = gendered(g, 'grandmother', 'grandfather', 'grandparent') + '-in-law';
      else if (b.up === 1 && b.down === 1 && !b.half) w = gendered(g, 'sister', 'brother', 'sibling') + '-in-law';
      else if (b.up === 0 && b.down === 1) w = 'step' + gendered(g, 'daughter', 'son', 'child');
      else if (b.up === 0 && b.down === 2) w = 'step-' + gendered(g, 'granddaughter', 'grandson', 'grandchild');
      if (w) {
        words.push(w);
        i += 2;
        continue;
      }
    }
    if (plainBlood(s0) && isMarriedLike(s1)) {
      const b = s0 as Extract<Segment, { kind: 'blood' }>;
      const g = gEnd(s1!);
      let w: string | undefined;
      if (b.up === 1 && b.down === 1 && !b.half) w = gendered(g, 'sister', 'brother', 'sibling') + '-in-law';
      else if (b.up === 0 && b.down === 1) w = gendered(g, 'daughter', 'son', 'child') + '-in-law';
      else if (b.up === 0 && b.down === 2) w = gendered(g, 'granddaughter', 'grandson', 'grandchild') + '-in-law';
      else if (b.up === 1 && b.down === 0) w = 'step' + gendered(g, 'mother', 'father', 'parent');
      else if (b.up === 2 && b.down === 0) w = 'step-' + gendered(g, 'grandmother', 'grandfather', 'grandparent');
      if (w) {
        words.push(w);
        i += 2;
        continue;
      }
    }
    words.push(segmentTerm(tree, s0));
    i++;
  }
  return words.join("'s ");
}

function segmentTerm(tree: Tree, s: Segment): string {
  const g = genderOf(tree, s.end);
  switch (s.kind) {
    case 'blood': return bloodSegmentTerm(s, g);
    case 'partner': return partnerTerm(s.rel, g, outlived(tree, s.rel, s.end));
    case 'social': return socialTerm(s.rel, s.start, s.end);
    case 'group': return `fellow ${GROUP_NOUN[s.group.kind]} member (${s.group.name})`;
    case 'coparent': return 'co-parent';
  }
}

// ---------------------------------------------------------------- virtual siblings

const VIRTUAL = '~sib:';
const isVirtual = (id: Id) => id.startsWith(VIRTUAL);

interface VirtualParent {
  vid: Id;
  type: ParentType;
  half: boolean;
  siblingType: SiblingType;
}

interface Ctx {
  g: Graph;
  virtualParents: Map<Id, VirtualParent[]>;
  virtualChildren: Map<Id, Id[]>;
  virtualInfo: Map<Id, VirtualParent>;
}

const SIB_TO_PARENT: Record<SiblingType, ParentType> = {
  unknown: 'biological',
  full: 'biological',
  half: 'biological',
  adoptive: 'adoptive',
  step: 'step',
  foster: 'foster',
  chosen: 'chosen',
};

const ctxCache = new WeakMap<Tree, Ctx>();

function ctxOf(tree: Tree): Ctx {
  const hit = ctxCache.get(tree);
  if (hit) return hit;
  const g = graphOf(tree);
  const virtualParents = new Map<Id, VirtualParent[]>();
  const virtualChildren = new Map<Id, Id[]>();
  const virtualInfo = new Map<Id, VirtualParent>();
  // Union-find per sibling type over explicit links between people who share no recorded parent.
  const parent = new Map<string, string>();
  const find = (x: string): string => {
    let r = x;
    while (parent.get(r) !== r) r = parent.get(r)!;
    let c = x;
    while (parent.get(c) !== r) {
      const n = parent.get(c)!;
      parent.set(c, r);
      c = n;
    }
    return r;
  };
  for (const r of Object.values(tree.relationships)) {
    if (r.kind !== 'sibling' || !tree.people[r.a] || !tree.people[r.b]) continue;
    const pa = parentsOf(g, r.a);
    const pb = new Set(parentsOf(g, r.b));
    if (pa.some((p) => pb.has(p))) continue;
    const ka = `${r.type}|${r.a}`;
    const kb = `${r.type}|${r.b}`;
    if (!parent.has(ka)) parent.set(ka, ka);
    if (!parent.has(kb)) parent.set(kb, kb);
    parent.set(find(ka), find(kb));
  }
  for (const key of parent.keys()) {
    const [type, person] = key.split('|') as [SiblingType, Id];
    const vid = VIRTUAL + find(key);
    const vp: VirtualParent = { vid, type: SIB_TO_PARENT[type], half: type === 'half', siblingType: type };
    virtualInfo.set(vid, vp);
    const list = virtualParents.get(person) ?? [];
    list.push(vp);
    virtualParents.set(person, list);
    const kids = virtualChildren.get(vid) ?? [];
    kids.push(person);
    virtualChildren.set(vid, kids);
  }
  const ctx = { g, virtualParents, virtualChildren, virtualInfo };
  ctxCache.set(tree, ctx);
  return ctx;
}

function upEdges(ctx: Ctx, id: Id): { to: Id; type: ParentType }[] {
  const out = (ctx.g.parentRels.get(id) ?? []).map((r) => ({ to: r.a, type: r.type }));
  for (const vp of ctx.virtualParents.get(id) ?? []) out.push({ to: vp.vid, type: vp.type });
  return out;
}

interface UpInfo {
  depth: number;
  path: Step[];
}

function upPaths(ctx: Ctx, start: Id, maxDepth = 40): Map<Id, UpInfo> {
  const res = new Map<Id, UpInfo>([[start, { depth: 0, path: [] }]]);
  let frontier = [start];
  for (let d = 1; d <= maxDepth && frontier.length; d++) {
    const next: Id[] = [];
    for (const x of frontier) {
      // Virtual parents have no parents of their own.
      if (isVirtual(x)) continue;
      const base = res.get(x)!;
      for (const e of upEdges(ctx, x)) {
        if (res.has(e.to)) continue;
        res.set(e.to, { depth: d, path: [...base.path, { kind: 'up', from: x, to: e.to, parentType: e.type }] });
        next.push(e.to);
      }
    }
    frontier = next;
  }
  return res;
}

function ancestorSet(ctx: Ctx, id: Id): Set<Id> {
  const s = new Set<Id>();
  const stack = [id];
  while (stack.length) {
    const x = stack.pop()!;
    if (isVirtual(x)) continue;
    for (const e of upEdges(ctx, x))
      if (!s.has(e.to)) {
        s.add(e.to);
        stack.push(e.to);
      }
  }
  return s;
}

function reverseUpPath(path: Step[]): Step[] {
  return path
    .slice()
    .reverse()
    .map((s) => ({ kind: 'down' as const, from: s.to, to: s.from, parentType: s.parentType }));
}

/** Replace "up to a virtual parent, down to a sibling" with a single sibling step for display. */
export function displayPath(ctx: { virtualInfo: Map<Id, VirtualParent> } | null, path: Step[]): Step[] {
  const out: Step[] = [];
  for (let i = 0; i < path.length; i++) {
    const s = path[i];
    if (s.kind === 'up' && isVirtual(s.to) && path[i + 1]?.kind === 'down') {
      const vp = ctx?.virtualInfo.get(s.to);
      out.push({ kind: 'sibling', from: s.from, to: path[i + 1].to, siblingType: vp?.siblingType ?? 'unknown' });
      i++;
      continue;
    }
    out.push(s);
  }
  return out;
}

// ---------------------------------------------------------------- blood relations

function bloodRelations(tree: Tree, ctx: Ctx, a: Id, b: Id): Relation[] {
  const ua = upPaths(ctx, a);
  const ub = upPaths(ctx, b);
  const common: Id[] = [];
  for (const k of ua.keys()) if (ub.has(k)) common.push(k);
  if (!common.length) return [];
  common.sort((x, y) => ua.get(x)!.depth + ub.get(x)!.depth - (ua.get(y)!.depth + ub.get(y)!.depth));

  const covered = new Set<Id>();
  const lcas: Id[] = [];
  for (const c of common) {
    if (covered.has(c)) continue;
    lcas.push(c);
    for (const anc of ancestorSet(ctx, c)) covered.add(anc);
  }

  const groups = new Map<string, Id[]>();
  for (const c of lcas) {
    const key = `${ua.get(c)!.depth},${ub.get(c)!.depth}`;
    const list = groups.get(key) ?? [];
    list.push(c);
    groups.set(key, list);
  }

  const out: Relation[] = [];
  for (const [key, cs] of groups) {
    const [da, db] = key.split(',').map(Number);
    // Prefer a real ancestor, and the most biological path, for the headline term
    // (Leo and Sam share a birth mother and an adoptive mother: they're siblings, not adoptive siblings).
    const nonBioCount = (c: Id) =>
      [...ua.get(c)!.path, ...ub.get(c)!.path].filter((s) => s.parentType && s.parentType !== 'biological' && s.parentType !== 'unknown').length;
    const primary = [...cs].sort((x, y) => Number(isVirtual(x)) - Number(isVirtual(y)) || nonBioCount(x) - nonBioCount(y))[0];
    const pa = ua.get(primary)!.path;
    const pb = ub.get(primary)!.path;
    const path = [...pa, ...reverseUpPath(pb)];
    const types = path.map((s) => s.parentType ?? 'biological');

    let half = false;
    let double = false;
    if (da >= 1 && db >= 1) {
      // The two people just below the common ancestor on each side.
      const pairs = new Set(cs.map((c) => {
        const ca = ua.get(c)!.path.at(-1)!.from;
        const cb = ub.get(c)!.path.at(-1)!.from;
        return `${ca}|${cb}`;
      }));
      if (cs.length === 1) {
        const c = cs[0];
        if (isVirtual(c)) half = ctx.virtualInfo.get(c)!.half;
        else {
          const ca = pa.at(-1)!.from;
          const cb = pb.at(-1)!.from;
          half = parentsOf(ctx.g, ca).length >= 2 && parentsOf(ctx.g, cb).length >= 2;
        }
      } else if (pairs.size >= 2 && !(da === 1 && db === 1)) {
        double = true;
      }
    }
    const seg: Segment = { kind: 'blood', up: da, down: db, end: b, types, half, double };
    const realLcas = cs.filter((c) => !isVirtual(c));
    const names = realLcas.map((c) => displayName(tree.people[c]));
    let explanation: string;
    if (da === 0) explanation = `${displayName(tree.people[b])} is ${db} generation${db === 1 ? '' : 's'} below ${displayName(tree.people[a])} in a direct line.`;
    else if (db === 0) explanation = `${displayName(tree.people[b])} is ${da} generation${da === 1 ? '' : 's'} above ${displayName(tree.people[a])} in a direct line.`;
    else if (!realLcas.length) explanation = 'They are recorded as siblings.';
    else
      explanation = `Closest common ancestor${names.length > 1 ? 's' : ''}: ${joinNames(names)}. ` +
        `${displayName(tree.people[a])} is ${da} generation${da === 1 ? '' : 's'} down from ${names.length > 1 ? 'them' : names[0]}; ` +
        `${displayName(tree.people[b])} is ${db}.`;
    out.push({
      category: 'blood',
      term: segmentsToTerm(tree, [seg]),
      path,
      segments: [seg],
      commonAncestors: realLcas,
      explanation,
      rank: da + db,
    });
  }
  return out.sort((x, y) => x.rank - y.rank);
}

export function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names.at(-1)}`;
}

// ---------------------------------------------------------------- shortest chain

function allEdges(ctx: Ctx, id: Id, withGroups: boolean): Step[] {
  const g = ctx.g;
  const out: Step[] = [];
  for (const r of g.parentRels.get(id) ?? []) out.push({ kind: 'up', from: id, to: r.a, parentType: r.type });
  for (const r of g.childRels.get(id) ?? []) out.push({ kind: 'down', from: id, to: r.b, parentType: r.type });
  for (const r of g.partnerRels.get(id) ?? []) out.push({ kind: 'partner', from: id, to: other(r, id), partner: r });
  for (const r of g.siblingRels.get(id) ?? []) out.push({ kind: 'sibling', from: id, to: other(r, id), siblingType: r.type });
  for (const r of g.socialRels.get(id) ?? []) out.push({ kind: 'social', from: id, to: other(r, id), social: r });
  if (withGroups)
    for (const grp of g.groupsOf.get(id) ?? [])
      for (const m of grp.members) if (m.personId !== id) out.push({ kind: 'group', from: id, to: m.personId, group: grp });
  return out;
}

export function shortestPath(tree: Tree, a: Id, b: Id, withGroups = true): Step[] | null {
  const ctx = ctxOf(tree);
  const tryOnce = (groups: boolean) => {
    const prev = new Map<Id, Step | null>([[a, null]]);
    let frontier = [a];
    while (frontier.length && !prev.has(b)) {
      const next: Id[] = [];
      for (const x of frontier)
        for (const e of allEdges(ctx, x, groups))
          if (!prev.has(e.to)) {
            prev.set(e.to, e);
            next.push(e.to);
          }
      frontier = next;
    }
    if (!prev.has(b)) return null;
    const path: Step[] = [];
    let cur = b;
    while (cur !== a) {
      const s = prev.get(cur)!;
      path.push(s);
      cur = s.from;
    }
    return path.reverse();
  };
  return tryOnce(false) ?? (withGroups ? tryOnce(true) : null);
}

/** Group a step path into segments: runs of up* down* become one blood segment. */
export function pathToSegments(path: Step[]): Segment[] {
  const segs: Segment[] = [];
  let cur: Extract<Segment, { kind: 'blood' }> | null = null;
  const flush = () => {
    if (cur) segs.push(cur);
    cur = null;
  };
  for (const s of path) {
    if (s.kind === 'up') {
      if (cur && cur.down > 0) flush();
      cur ??= { kind: 'blood', up: 0, down: 0, end: s.to, types: [] };
      cur.up++;
      cur.end = s.to;
      cur.types.push(s.parentType ?? 'biological');
    } else if (s.kind === 'down') {
      cur ??= { kind: 'blood', up: 0, down: 0, end: s.to, types: [] };
      cur.down++;
      cur.end = s.to;
      cur.types.push(s.parentType ?? 'biological');
    } else if (s.kind === 'sibling') {
      // A sibling hop is "one up, one down" through an unrecorded shared parent.
      if (cur && cur.down > 0) flush();
      cur ??= { kind: 'blood', up: 0, down: 0, end: s.to, types: [] };
      cur.up++;
      cur.down++;
      cur.end = s.to;
      cur.types.push(SIB_TO_PARENT[s.siblingType ?? 'unknown']);
      if (s.siblingType === 'half') cur.half = true;
    } else {
      flush();
      if (s.kind === 'partner') segs.push({ kind: 'partner', end: s.to, rel: s.partner! });
      else if (s.kind === 'social') segs.push({ kind: 'social', end: s.to, start: s.from, rel: s.social! });
      else if (s.kind === 'group') segs.push({ kind: 'group', end: s.to, group: s.group! });
    }
  }
  flush();
  // "child's other parent" reads better as co-parent.
  const merged: Segment[] = [];
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    const n = segs[i + 1];
    if (s.kind === 'blood' && n?.kind === 'blood' && s.up === 0 && s.down === 1 && n.up === 1 && n.down === 0) {
      merged.push({ kind: 'coparent', end: n.end });
      i++;
    } else merged.push(s);
  }
  return merged;
}

// ---------------------------------------------------------------- main entry

export function relate(tree: Tree, a: Id, b: Id): KinshipResult {
  const result: KinshipResult = { a, b, relations: [] };
  if (!tree.people[a] || !tree.people[b]) return result;
  if (a === b) {
    result.relations.push({ category: 'self', term: 'self', path: [], segments: [], commonAncestors: [], explanation: 'Same person.', rank: RANK.self });
    return result;
  }
  const ctx = ctxOf(tree);
  const g = ctx.g;
  const nameA = displayName(tree.people[a]);
  const nameB = displayName(tree.people[b]);
  const rels: Relation[] = [];

  // 1. Blood / legal kinship.
  const blood = bloodRelations(tree, ctx, a, b);
  rels.push(...blood);

  // 2. Direct partners.
  for (const r of g.partnerRels.get(a) ?? []) {
    if (other(r, a) !== b) continue;
    const seg: Segment = { kind: 'partner', end: b, rel: r };
    rels.push({
      category: 'partner',
      term: segmentsToTerm(tree, [seg]),
      path: [{ kind: 'partner', from: a, to: b, partner: r }],
      segments: [seg],
      commonAncestors: [],
      explanation: `${nameA} and ${nameB} are recorded as partners.`,
      rank: r.status === 'current' || r.status === 'widowed' ? RANK.partner : RANK.formerPartner,
    });
  }

  // 3. Direct social ties.
  for (const r of g.socialRels.get(a) ?? []) {
    if (other(r, a) !== b) continue;
    const seg: Segment = { kind: 'social', end: b, start: a, rel: r };
    rels.push({
      category: 'social',
      term: segmentsToTerm(tree, [seg]),
      path: [{ kind: 'social', from: a, to: b, social: r }],
      segments: [seg],
      commonAncestors: [],
      explanation: 'A direct connection is recorded between them.',
      rank: r.type === 'best-friend' ? RANK.bestFriend : RANK.connection,
    });
  }

  // Explicit sibling links that the blood pass couldn't see (people who also share a parent).
  if (!blood.some((x) => x.rank === RANK.recordedSibling && x.segments[0]?.kind === 'blood' && x.segments[0].up === 1)) {
    for (const s of siblingsOf(g, a)) {
      if (s.id !== b || !s.explicit) continue;
      const step: Step = { kind: 'sibling', from: a, to: b, siblingType: s.type };
      const segs = pathToSegments([step]);
      rels.push({ category: 'blood', term: segmentsToTerm(tree, segs), path: [step], segments: segs, commonAncestors: [], explanation: 'They are recorded as siblings.', rank: RANK.recordedSibling });
    }
  }

  // 4. In-law and step relations: partner hop + blood relation, either end.
  const bloodCloseEnough = (r: Relation) => r.rank <= RANK.inLawReach;
  for (const pr of g.partnerRels.get(a) ?? []) {
    const p = other(pr, a);
    if (p === b) continue;
    for (const br of bloodRelations(tree, ctx, p, b).filter(bloodCloseEnough).slice(0, 1)) {
      // Your partner's descendant through a child you share is your own descendant, not a
      // step-one: your partner's child who is also your child, their grandchild through your son.
      const s0 = br.segments[0];
      const below = br.path[0];
      if (s0?.kind === 'blood' && s0.up === 0 && below?.kind === 'down' && parentsOf(g, below.to).includes(a)) continue;
      const segs: Segment[] = [{ kind: 'partner', end: p, rel: pr }, ...br.segments];
      rels.push({
        category: 'in-law',
        term: segmentsToTerm(tree, segs),
        path: [{ kind: 'partner', from: a, to: p, partner: pr }, ...br.path],
        segments: segs,
        commonAncestors: br.commonAncestors,
        explanation: `${nameB} is related to ${nameA}'s ${segmentsToTerm(tree, [segs[0]])}, ${displayName(tree.people[p])}.`,
        rank: br.rank + RANK.inLaw + (MARRIED_LIKE.has(pr.type) ? 0 : RANK.unmarriedInLaw),
      });
    }
  }
  for (const pr of g.partnerRels.get(b) ?? []) {
    const q = other(pr, b);
    if (q === a) continue;
    for (const br of bloodRelations(tree, ctx, a, q).filter(bloodCloseEnough).slice(0, 1)) {
      // An ancestor's partner who is also a parent of the next person down that line is a
      // co-ancestor by blood, not a step-one: your father's father, married to his mother, is
      // your grandfather, not your step-grandfather. (Married to your other grandmother, he's both.)
      const s1 = br.segments[0];
      const above = br.path.at(-1);
      if (s1?.kind === 'blood' && s1.down === 0 && above?.kind === 'up' && parentsOf(g, above.from).includes(b)) continue;
      const segs: Segment[] = [...br.segments, { kind: 'partner', end: b, rel: pr }];
      rels.push({
        category: 'in-law',
        term: segmentsToTerm(tree, segs),
        path: [...br.path, { kind: 'partner', from: q, to: b, partner: pr }],
        segments: segs,
        commonAncestors: br.commonAncestors,
        explanation: `${nameB} is the ${segmentsToTerm(tree, [segs.at(-1)!])} of ${nameA}'s ${br.term}, ${displayName(tree.people[q])}.`,
        rank: br.rank + RANK.inLaw + (MARRIED_LIKE.has(pr.type) ? 0 : RANK.unmarriedInLaw),
      });
    }
  }
  // Stepsiblings: A's parent's partner's child.
  const aParents = parentsOf(g, a);
  const bParents = new Set(parentsOf(g, b));
  if (!aParents.some((p) => bParents.has(p))) {
    for (const x of aParents)
      for (const pr of g.partnerRels.get(x) ?? []) {
        const y = other(pr, x);
        if (!childrenOf(g, y).includes(b)) continue;
        const path: Step[] = [
          { kind: 'up', from: a, to: x, parentType: 'biological' },
          { kind: 'partner', from: x, to: y, partner: pr },
          { kind: 'down', from: y, to: b, parentType: 'biological' },
        ];
        const segs = pathToSegments(path);
        rels.push({
          category: 'in-law',
          term: segmentsToTerm(tree, segs),
          path,
          segments: segs,
          commonAncestors: [],
          explanation: `${nameB}'s parent ${displayName(tree.people[y])} is the partner of ${nameA}'s parent ${displayName(tree.people[x])}.`,
          rank: RANK.stepsibling,
        });
      }
  }

  // 5. Family friends: B is a family friend of someone in A's close family.
  const close = new Set<Id>([a, ...aParents, ...childrenOf(g, a), ...siblingsOf(g, a).map((s) => s.id)]);
  for (const p of aParents) for (const gp of parentsOf(g, p)) close.add(gp);
  for (const pr of g.partnerRels.get(a) ?? []) close.add(other(pr, a));
  for (const r of g.socialRels.get(b) ?? []) {
    if (r.type !== 'family-friend') continue;
    const f = other(r, b);
    if (!close.has(f) || f === a) continue;
    // Not relate(): two family friends linked to each other would recurse forever.
    const pr = (g.partnerRels.get(a) ?? []).find((x) => other(x, a) === f);
    const sub: Pick<Relation, 'term' | 'path' | 'segments'> | undefined = pr
      ? { term: partnerTerm(pr, genderOf(tree, f), outlived(tree, pr, f)), path: [{ kind: 'partner', from: a, to: f, partner: pr }], segments: [{ kind: 'partner', end: f, rel: pr }] }
      : bloodRelations(tree, ctx, a, f)[0];
    rels.push({
      category: 'family-friend',
      term: 'family friend',
      path: [...(sub?.path ?? []), { kind: 'social', from: f, to: b, social: r }],
      segments: [...(sub?.segments ?? []), { kind: 'social', end: b, start: f, rel: r }],
      commonAncestors: [],
      explanation: `${nameB} is a friend of the family through ${nameA}'s ${sub?.term ?? 'relative'}, ${displayName(tree.people[f])}.`,
      rank: RANK.familyFriend,
    });
    break;
  }

  // 6. Shared groups.
  const bGroups = new Set((g.groupsOf.get(b) ?? []).map((x) => x.id));
  for (const grp of g.groupsOf.get(a) ?? []) {
    if (!bGroups.has(grp.id)) continue;
    const seg: Segment = { kind: 'group', end: b, group: grp };
    const foundFamily = grp.kind === 'found-family';
    rels.push({
      category: 'group',
      term: foundFamily ? `found family (${grp.name})` : segmentsToTerm(tree, [seg]),
      path: [{ kind: 'group', from: a, to: b, group: grp }],
      segments: [seg],
      commonAncestors: [],
      explanation: `Both are members of ${grp.name}.`,
      rank: foundFamily ? RANK.foundFamily : RANK.sharedGroup,
    });
  }

  // 7. Fallback: shortest chain over every kind of link.
  if (!rels.length) {
    const path = shortestPath(tree, a, b);
    if (path) {
      const segs = pathToSegments(path);
      rels.push({
        category: 'chain',
        term: segmentsToTerm(tree, segs),
        path,
        segments: segs,
        commonAncestors: [],
        explanation: `Connected through ${path.length - 1} other ${path.length - 1 === 1 ? 'person' : 'people'}.`,
        rank: RANK.chain + path.length,
      });
    }
  }

  // De-duplicate identical terms, keep the closest.
  const seen = new Set<string>();
  result.relations = rels
    .sort((x, y) => x.rank - y.rank)
    .filter((r) => {
      if (seen.has(r.term)) return false;
      seen.add(r.term);
      return true;
    })
    .map((r) => ({ ...r, path: displayPath(ctx, r.path) }));
  return result;
}

/** One-line answer, e.g. "Sam is Alex's first cousin once removed." */
/** A direct tie: parent, child, sibling, partner, or friend-type connection (not via anyone else). */
function isDirect(r: Relation): boolean {
  if (r.segments.length !== 1) return false;
  const s = r.segments[0];
  if (s.kind === 'blood') return s.up <= 1 && s.down <= 1 && s.up + s.down > 0;
  return s.kind === 'partner' || s.kind === 'social';
}

/**
 * The relations to lead with: the closest one, plus every other direct tie when the closest
 * is direct too, so someone who is both a best friend and a chosen sister is described as both.
 */
export function headlineRelations(result: KinshipResult): Relation[] {
  const [first, ...rest] = result.relations;
  if (!first) return [];
  if (!isDirect(first)) return [first];
  const terms = new Set([first.term]);
  return [first, ...rest.filter((r) => isDirect(r) && !terms.has(r.term) && !!terms.add(r.term))];
}

/** "best friend and chosen sister". */
export function headlineTerm(result: KinshipResult): string {
  return joinNames(headlineRelations(result).map((r) => r.term));
}

export function relationSentence(tree: Tree, a: Id, b: Id, rel: Relation | undefined): string {
  const na = displayName(tree.people[a]);
  const nb = displayName(tree.people[b]);
  if (!rel) return `No connection found between ${na} and ${nb}.`;
  if (rel.category === 'self') return `That's the same person.`;
  return `${nb} is ${na}'s ${rel.term}.`;
}

/** Short label for a single step, from `to`'s perspective relative to `from`. */
export function stepLabel(tree: Tree, s: Step): string {
  const segs = pathToSegments([s]);
  return segs.length ? segmentsToTerm(tree, segs) : '';
}
